import React, { useState, useEffect } from 'react'
import { useSuperAdmin } from '../hooks/useSuperAdmin'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import {
  Users,
  Activity,
  CreditCard,
  TrendingUp,
  Shield,
  AlertCircle,
  CheckCircle,
  XCircle,
  Gift,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
} from 'lucide-react'

interface User {
  id: string
  email: string
  full_name: string
  role: string
  plan_type: string
  credits_used: number
  credits_limit: number
  is_suspended: boolean
  last_reset_date: string
  created_at: string
  last_activity: string | null
}

export function SuperAdminDashboard() {
  const {
    isSuperAdmin,
    loading,
    getPlatformStats,
    getAllUsers,
    updateUserPlan,
    resetUserCredits,
    toggleSuspension,
    grantBonusCredits,
    deleteUser,
  } = useSuperAdmin()

  const [stats, setStats] = useState<any>(null)
  const [users, setUsers] = useState<User[]>([])
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const [loadingAction, setLoadingAction] = useState(false)
  const pageSize = 20

  // Load initial data
  useEffect(() => {
    if (isSuperAdmin) {
      loadDashboardData()
    }
  }, [isSuperAdmin])

  const loadDashboardData = async () => {
    const [statsData, usersData] = await Promise.all([
      getPlatformStats(),
      getAllUsers(pageSize, currentPage * pageSize),
    ])

    if (statsData) setStats(statsData)
    if (usersData) setUsers(usersData)
  }

  const handlePageChange = async (direction: 'prev' | 'next') => {
    const newPage = direction === 'next' ? currentPage + 1 : currentPage - 1
    if (newPage < 0) return

    setCurrentPage(newPage)
    const usersData = await getAllUsers(pageSize, newPage * pageSize)
    if (usersData) setUsers(usersData)
  }

  const handleUpdatePlan = async (userId: string, newPlan: string, newLimit: number) => {
    setLoadingAction(true)
    const result = await updateUserPlan(userId, newPlan as any, newLimit)
    if (result.success) {
      alert('Plan updated successfully!')
      await loadDashboardData()
    } else {
      alert(`Error: ${result.error}`)
    }
    setLoadingAction(false)
    setSelectedUser(null)
  }

  const handleResetCredits = async (userId: string) => {
    if (!confirm("Are you sure you want to reset this user's credits?")) return

    setLoadingAction(true)
    const result = await resetUserCredits(userId, 'Manual reset by admin')
    if (result.success) {
      alert('Credits reset successfully!')
      await loadDashboardData()
    } else {
      alert(`Error: ${result.error}`)
    }
    setLoadingAction(false)
  }

  const handleToggleSuspension = async (userId: string, suspend: boolean) => {
    const action = suspend ? 'suspend' : 'unsuspend'
    if (!confirm(`Are you sure you want to ${action} this user?`)) return

    setLoadingAction(true)
    const result = await toggleSuspension(userId, suspend, `Manual ${action} by admin`)
    if (result.success) {
      alert(`User ${action}ed successfully!`)
      await loadDashboardData()
    } else {
      alert(`Error: ${result.error}`)
    }
    setLoadingAction(false)
  }

  const handleGrantBonus = async (userId: string, amount: number) => {
    setLoadingAction(true)
    const result = await grantBonusCredits(userId, amount, 'Bonus credits from admin')
    if (result.success) {
      alert('Bonus credits granted successfully!')
      await loadDashboardData()
    } else {
      alert(`Error: ${result.error}`)
    }
    setLoadingAction(false)
    setSelectedUser(null)
  }

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    const confirmed = confirm(
      `⚠️ WARNING: This will PERMANENTLY delete the user account and ALL associated data.\n\n` +
      `User: ${userEmail}\n\n` +
      `This includes:\n` +
      `- Profile information\n` +
      `- All activities\n` +
      `- All content library items\n` +
      `- All tool configurations\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Are you absolutely sure you want to delete this user?`
    )

    if (!confirmed) return

    // Double confirmation for safety
    const doubleConfirm = confirm(
      `FINAL CONFIRMATION\n\n` +
      `Type YES in the next prompt to confirm deletion of: ${userEmail}`
    )

    if (!doubleConfirm) return

    setLoadingAction(true)
    const result = await deleteUser(userId, 'Deleted by admin via dashboard')

    if (result.success) {
      alert(`User ${userEmail} has been permanently deleted.\n\nDeleted data:\n- ${result.data.activities_deleted} activities\n- ${result.data.content_deleted} content items`)
      setSelectedUser(null)
      await loadDashboardData()
    } else {
      alert(`Error deleting user: ${result.error}`)
    }
    setLoadingAction(false)
  }

  // Filter users by search query
  const filteredUsers = users.filter((user) =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading SuperAdmin Dashboard...</p>
        </div>
      </div>
    )
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="p-8 max-w-md text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
          <p className="text-slate-600">
            You don't have permission to access the SuperAdmin Dashboard.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="h-8 w-8 text-purple-600" />
            SuperAdmin Dashboard
          </h1>
          <p className="text-slate-600 mt-1">Platform management and analytics</p>
        </div>
        <Button onClick={loadDashboardData} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Platform Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Users</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_users}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Free: {stats.free_users} | Pro: {stats.pro_users} | Enterprise: {stats.enterprise_users}
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Credits Used</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {stats.total_credits_used?.toLocaleString() || 0}
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <CreditCard className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Across all users and tools
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Activities Today</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{stats.activities_today}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Activity className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Total: {stats.total_activities?.toLocaleString()}
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">New Users (Month)</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{stats.new_users_this_month}</p>
              </div>
              <div className="bg-amber-100 p-3 rounded-full">
                <TrendingUp className="h-6 w-6 text-amber-600" />
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Suspended: {stats.suspended_users}
            </div>
          </Card>
        </div>
      )}

      {/* User Management */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-slate-900">User Management</h2>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">User</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Plan</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Credits</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Last Active</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-slate-900">{user.email}</p>
                      <p className="text-xs text-slate-500">{user.full_name || 'No name'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.plan_type === 'enterprise'
                          ? 'bg-purple-100 text-purple-700'
                          : user.plan_type === 'pro'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {user.plan_type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <span className="font-medium">{user.credits_used}</span>
                      <span className="text-slate-500"> / {user.credits_limit}</span>
                    </div>
                    <div className="w-24 bg-slate-200 rounded-full h-1.5 mt-1">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full"
                        style={{
                          width: `${Math.min((user.credits_used / user.credits_limit) * 100, 100)}%`,
                        }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {user.is_suspended ? (
                      <span className="inline-flex items-center gap-1 text-red-600">
                        <XCircle className="h-4 w-4" />
                        Suspended
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {user.last_activity
                      ? new Date(user.last_activity).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Manage
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
          <p className="text-sm text-slate-600">
            Showing {currentPage * pageSize + 1} to{' '}
            {Math.min((currentPage + 1) * pageSize, users.length)} of {users.length} users
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => handlePageChange('prev')}
              disabled={currentPage === 0}
              variant="outline"
              className="flex items-center gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              onClick={() => handlePageChange('next')}
              disabled={users.length < pageSize}
              variant="outline"
              className="flex items-center gap-1"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* User Management Modal */}
      {selectedUser && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedUser(null)}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900">Manage User</h3>
              <p className="text-sm text-slate-600 mt-1">{selectedUser.email}</p>
            </div>

            <div className="p-6 space-y-4">
              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => handleResetCredits(selectedUser.id)}
                  disabled={loadingAction}
                  variant="outline"
                  className="flex items-center justify-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reset Credits
                </Button>
                <Button
                  onClick={() =>
                    handleToggleSuspension(selectedUser.id, !selectedUser.is_suspended)
                  }
                  disabled={loadingAction}
                  variant="outline"
                  className="flex items-center justify-center gap-2"
                >
                  {selectedUser.is_suspended ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Unsuspend
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" />
                      Suspend
                    </>
                  )}
                </Button>
              </div>

              {/* Grant Bonus */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Grant Bonus Credits
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    id="bonus-amount"
                    placeholder="Amount"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                  <Button
                    onClick={() => {
                      const input = document.getElementById('bonus-amount') as HTMLInputElement
                      const amount = parseInt(input.value)
                      if (amount > 0) {
                        handleGrantBonus(selectedUser.id, amount)
                      }
                    }}
                    disabled={loadingAction}
                  >
                    <Gift className="h-4 w-4 mr-2" />
                    Grant
                  </Button>
                </div>
              </div>

              {/* Update Plan */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Update Plan
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['free', 'pro', 'enterprise'].map((plan) => (
                    <button
                      key={plan}
                      onClick={() => {
                        const limits = { free: 1000, pro: 10000, enterprise: 999999 }
                        handleUpdatePlan(selectedUser.id, plan, limits[plan as keyof typeof limits])
                      }}
                      disabled={loadingAction || selectedUser.plan_type === plan}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedUser.plan_type === plan
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {plan.charAt(0).toUpperCase() + plan.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Danger Zone - Delete User */}
              <div className="mt-6 pt-6 border-t border-red-200">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-red-900 mb-1">
                        Danger Zone
                      </h4>
                      <p className="text-sm text-red-700 mb-3">
                        Permanently delete this user account and all associated data. This action cannot be undone.
                      </p>
                      <Button
                        onClick={() => handleDeleteUser(selectedUser.id, selectedUser.email)}
                        disabled={loadingAction}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete User Permanently
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-between items-center">
              <p className="text-xs text-slate-500">
                User ID: {selectedUser.id.slice(0, 8)}...
              </p>
              <Button onClick={() => setSelectedUser(null)} variant="outline">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
